import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import type { AxiosResponse } from 'axios';
import type { FC } from 'react';

import { KBApi } from '../api';
import PageHeader from '../components/PageHeader';
import PageLoading from '../components/PageLoading';
import PageNav from '../components/PageNav';
import TopicsList from '../components/TopicsList';
import { useDelayedFlag } from '../utils/useDelayedFlag';

import type { Crumb } from '../components/Breadcrumbs';
import type { Section } from '../types';

// A section page: the tag category's own name and description, then a card per
// topic inside it.
const ViewSection: FC = () => {
  const { t } = useTranslation();
  const params = useParams();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  // The loaded page keeps the view hash it was fetched with. Links and
  // breadcrumbs are built from that hash, not from the current route, so a
  // page waiting for its replacement never mixes the two.
  const [section, setSection] = useState<{ viewHash: string; data: Section } | null>(null);

  // Every request is tagged with the route that asked for it. A response for
  // any other route is dropped, so a slow earlier request cannot overwrite the
  // page the reader is looking at, and a view hash change cannot reuse the
  // previous view's data.
  const routeKey = `${params.hash}/${params.id}`;
  const currentRoute = useRef(routeKey);

  // The ref moves on as soon as the route does, before any effect runs. Setting
  // it inside the request would leave a window where a response for the route
  // just left still matches, and would render under the new address.
  useLayoutEffect(() => {
    currentRoute.current = routeKey;
  }, [routeKey]);

  const fetchSection = useCallback(async () => {
    setIsLoading(true);

    try {
      const viewId = params.hash;
      const sectionId = params.id;
      if (!viewId || !sectionId) {
        throw new Error('Section route is missing the view hash or the section id');
      }
      const response: AxiosResponse<Section> = await KBApi.getSectionById(viewId, sectionId);
      if (currentRoute.current !== routeKey) {
        return;
      }
      setSection({ viewHash: viewId, data: response.data });
      setError(null);
    } catch (err) {
      if (currentRoute.current !== routeKey) {
        return;
      }
      // The retained section belongs to the route we navigated away from, so it
      // has to go. Leaving it on screen would show the reader a page that the
      // address bar no longer describes.
      setSection(null);
      setError(t('unknown_error'));
    } finally {
      if (currentRoute.current === routeKey) {
        setIsLoading(false);
      }
    }
  }, [routeKey, params.hash, params.id, t]);

  useEffect(() => {
    fetchSection();
  }, [fetchSection]);

  // The previous section stays on screen while the next one loads, so clicking
  // through the navigation does not blank the page.
  const isSlow = useDelayedFlag(isLoading);

  if (error) {
    return <button onClick={fetchSection}>{t('try_again')}</button>;
  }

  if (!section) {
    return isSlow ? <PageLoading /> : null;
  }

  const { viewHash, data } = section;
  const crumbs: Crumb[] = [{ label: t('breadcrumbs.home'), to: `/v/${viewHash}` }, { label: data.name }];

  return (
    <div aria-busy={isLoading} style={{ opacity: isSlow ? 0.6 : 1, transition: 'opacity 150ms' }}>
      <PageNav crumbs={crumbs} fallbackTo="../" />
      <PageHeader title={data.name} description={data.description} />
      <TopicsList viewHash={viewHash} sectionId={data.id} topics={data.topics} />
    </div>
  );
};

export default ViewSection;
